import { Button } from "components/ui/Button";
import { type BadgeTone, StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import type { MatchingResultListItem } from "hooks/use-matching-results-api";
import { ExternalLink } from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router-dom";

type MatchingResultsTableProps = {
  results: MatchingResultListItem[];
};

const TRIGGER_TYPE_BADGE: Record<string, { label: string; tone: BadgeTone }> = {
  freetext: { label: "フリー", tone: "tag-neutral" },
  manual: { label: "手動", tone: "tag-neutral" },
  preset: { label: "プリセット", tone: "tag-neutral" },
  auto: { label: "自動", tone: "soft-info" },
};

const SOURCE_TYPE_BADGE: Record<string, { label: string; tone: BadgeTone }> = {
  hr: { label: "人材", tone: "tag-neutral" },
  matter: { label: "案件", tone: "tag-neutral" },
};

const STATUS_BADGE: Record<string, { label: string; tone: BadgeTone }> = {
  new: { label: "新規", tone: "soft-info" },
  viewed: { label: "閲覧済", tone: "soft-neutral" },
  proposed: { label: "提案済", tone: "soft-success" },
  dismissed: { label: "却下", tone: "soft-error" },
};

/**
 * total depth から tone を返す
 * @param {number} total total depth
 * @return {BadgeTone} tone
 */
function depthTone(total: number): BadgeTone {
  if (total <= 2) return "soft-success";
  if (total === 3) return "soft-info";
  if (total === 4) return "soft-warning";
  return "soft-error";
}

/**
 * snapshot から total depth を計算する
 * @param {MatchingResultListItem} result マッチング結果
 * @return {number | null} total depth (算出不能時は null)
 */
function getTotalDepth(result: MatchingResultListItem): number | null {
  const isMatterSource = result.sourceType === "matter";
  const matterSnap = (
    isMatterSource ? result.sourceSnapshot : result.matchedSnapshot
  ) as Record<string, unknown> | null;
  const hrSnap = (
    isMatterSource ? result.matchedSnapshot : result.sourceSnapshot
  ) as Record<string, unknown> | null;
  const matterDepth = (matterSnap?.depth as number | null) ?? null;
  const hrDepth = (hrSnap?.depth as number | null) ?? null;
  if (matterDepth != null && hrDepth != null) return matterDepth + hrDepth;
  return null;
}

/**
 * スコアに応じたバッジクラスを返す
 * @param {number} score スコア (0〜1)
 * @return {string} badge class
 */
function scoreClass(score: number): string {
  if (score >= 0.8) return "text-success";
  if (score >= 0.6) return "text-warning";
  return "text-error";
}

/**
 * 日付を相対表示またはフォーマットする
 * @param {string} dateStr ISO日付文字列
 * @return {string} フォーマット済み日付
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "今日";
  if (diffDays === 1) return "昨日";
  if (diffDays < 7) return `${diffDays}日前`;

  return `${date.getMonth() + 1}/${date.getDate()}`;
}

/**
 * 結果に対する導線リンクのパスを返す
 * @param {MatchingResultListItem} result マッチング結果
 * @return {string | null} リンクパス (null の場合はリンクなし)
 */
function getLinkPath(result: MatchingResultListItem): string | null {
  switch (result.triggerType) {
    case "manual":
    case "auto":
      return result.sourceEmailId
        ? `/emails/search/${result.sourceEmailId}`
        : null;
    case "preset":
      return result.matchedPresetId
        ? `/settings/presets/${result.matchedPresetId}`
        : null;
    default:
      return null;
  }
}

/**
 * マッチング結果テーブル
 * @param {MatchingResultsTableProps} props コンポーネントProps
 * @return {JSX.Element} MatchingResultsTable コンポーネント
 */
export const MatchingResultsTable: FC<MatchingResultsTableProps> = ({
  results,
}) => {
  if (results.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm py-12 text-center text-base-content/60">
        <p>マッチング結果がありません</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <Table.Root density="compact" zebra stickyHeader>
        <Table.Head sticky>
          <tr>
            <Table.HeaderCell>マッチ元</Table.HeaderCell>
            <Table.HeaderCell>マッチ先</Table.HeaderCell>
            <Table.HeaderCell>種別</Table.HeaderCell>
            <Table.HeaderCell>ソース</Table.HeaderCell>
            <Table.HeaderCell>スコア</Table.HeaderCell>
            <Table.HeaderCell>商流</Table.HeaderCell>
            <Table.HeaderCell>ステータス</Table.HeaderCell>
            <Table.HeaderCell>日時</Table.HeaderCell>
            <Table.HeaderCell> </Table.HeaderCell>
          </tr>
        </Table.Head>
        <Table.Body>
          {results.map(result => {
            const trigger = TRIGGER_TYPE_BADGE[result.triggerType] || {
              label: result.triggerType,
              tone: "soft-neutral" as BadgeTone,
            };
            const source = SOURCE_TYPE_BADGE[result.sourceType] || {
              label: result.sourceType,
              tone: "tag-neutral" as BadgeTone,
            };
            const status = STATUS_BADGE[result.status] || {
              label: result.status,
              tone: "soft-neutral" as BadgeTone,
            };
            const linkPath = getLinkPath(result);
            const scorePercent = Math.round(result.totalScore * 100);

            return (
              <Table.Row key={result.id}>
                <Table.Cell>
                  <span className="truncate block max-w-48">
                    {result.sourceSubject ||
                      (result.sourceSnapshot as Record<string, string>)
                        ?.subject ||
                      "(フリーテキスト)"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="truncate block max-w-48">
                    {result.matchedSubject}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge tone={trigger.tone}>{trigger.label}</StatusBadge>
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge tone={source.tone}>{source.label}</StatusBadge>
                </Table.Cell>
                <Table.Cell>
                  <span
                    className={`font-mono font-semibold ${scoreClass(result.totalScore)}`}
                  >
                    {scorePercent}%
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {(() => {
                    const total = getTotalDepth(result);
                    if (total == null) {
                      return (
                        <StatusBadge tone="soft-neutral">商流 --</StatusBadge>
                      );
                    }
                    return (
                      <StatusBadge tone={depthTone(total)}>
                        商流 {total}
                      </StatusBadge>
                    );
                  })()}
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-base-content/60">
                    {formatDate(result.createdAt)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {linkPath && (
                    <Button
                      asChild
                      variant="ghost"
                      size="xs"
                      title="詳細を表示"
                    >
                      <Link to={linkPath}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </div>
  );
};
